import { memo, useMemo } from 'react'

import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { LoadingIndicator, NotFound } from '@shared/layouts'
import { StateVisualizer } from 'domains/Workspaces/Events/layouts'
import { eventQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'

import { CameraStatusRow } from './CameraStatusRow'
import { GlobalAudioStatusRow } from './GlobalAudioStatusRow'

export type StatusOverviewProps = {
  eventId: string
  /** Hide the event name + state header (e.g. when the dashboard renders it as its own section). */
  hideHeader?: boolean
  /** Size to content instead of filling the parent (dashboard) — avoids empty space below the list. */
  fitContent?: boolean
}

/** Read-only "simple" status view: resolved per-camera thumbnails + linked stream + on/off. */
const StatusOverviewRaw = ({ eventId, hideHeader, fitContent }: StatusOverviewProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const eventQuery = useQuery({
    ...eventQueryKeys.detail({ eventId }),
    refetchInterval: RefetchIntervals.FAST,
  })

  // trackId -> streamId (from status video tracks), to resolve each camera's stream.
  const trackToStream = useMemo(() => {
    const map = new Map<string, string>()
    for (const track of eventQuery.data?.status?.videoTracks ?? []) {
      const streamId = track.streamSources?.[0]?.streamId
      if (track.id && streamId) {
        map.set(track.id, streamId)
      }
    }
    return map
  }, [eventQuery.data?.status])

  // trackId -> streamId (from status audio tracks), to resolve each global audio's stream.
  const audioTrackToStream = useMemo(() => {
    const map = new Map<string, string>()
    for (const track of eventQuery.data?.status?.audioTracks ?? []) {
      const streamId = track.source?.stream?.streamId ?? track.streamSources?.[0]?.streamId
      if (track.id && streamId) {
        map.set(track.id, streamId)
      }
    }
    return map
  }, [eventQuery.data?.status])

  // Keep showing the indicator while a transient failure is being retried (e.g. a 401 that the
  // global handler refetches after a token refresh) — otherwise the 404 page flashes in between.
  if (eventQuery.isPending || (!eventQuery.data && eventQuery.isFetching)) {
    return <LoadingIndicator />
  }

  if (!eventQuery.data) {
    return <NotFound />
  }

  const event = eventQuery.data
  const entities = event.status?.entities ?? []
  const globalAudios = event.status?.globalAudios ?? []

  return (
    <Box sx={{ p: 3, height: fitContent ? 'auto' : '100%', overflowY: fitContent ? 'visible' : 'auto' }}>
      {!hideHeader && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="heading-md" noWrap>
            {event.status?.name || t('noNameProvided')}
          </Typography>
          {event.state != null && event.health != null && (
            <StateVisualizer variant="text-sm" state={event.state} health={event.health}>
              {event.state}
            </StateVisualizer>
          )}
        </Stack>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="heading-md" sx={{ mb: 1 }}>
          {t('domain:Workspace.Event.ConfigEditor.cameras')}
        </Typography>
        {!entities.length ? (
          <Typography variant="text-sm" color="text.secondary" sx={{ py: 2, display: 'block' }}>
            {t('domain:Workspace.Event.ConfigEditor.noCameras')}
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {entities.map((entity) => {
              const content = entity.videoContents?.[0]
              const trackId = content?.videoLinks?.[0]?.trackId
              return (
                <CameraStatusRow
                  key={entity.id}
                  name={entity.name}
                  enabled={entity.enabled}
                  resolvedThumbnailUrl={content?.resolvedThumbnail?.variants?.[0]?.url ?? undefined}
                  streamId={trackId != null ? trackToStream.get(trackId) : undefined}
                />
              )
            })}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="heading-md" sx={{ mb: 1 }}>
          {t('domain:Workspace.Event.ConfigEditor.globalAudio')}
        </Typography>
        {!globalAudios.length ? (
          <Typography variant="text-sm" color="text.secondary" sx={{ py: 2, display: 'block' }}>
            {t('domain:Workspace.Event.ConfigEditor.noGlobalAudio')}
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {globalAudios.map((globalAudio) => {
              const trackId = globalAudio.audioLinks?.[0]?.trackId
              return (
                <GlobalAudioStatusRow
                  key={globalAudio.id}
                  name={globalAudio.name}
                  enabled={!globalAudio.disable}
                  streamId={trackId != null ? audioTrackToStream.get(trackId) : undefined}
                />
              )
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  )
}

export const StatusOverview = memo(StatusOverviewRaw)
